import queryString from "query-string";
import { referralRequests } from "../constants/actionTypes";
import { EMPTY_IMPORT_READY, normalizeImportReady } from "../helpers/referralImport";

const defaultPreloaders = {
  preloaderCase: false,
  preloaderCaseImport: false,
  importReady: false,
  modal: false
};

const defaultModals = {
  modalReferralRequest: true,
  importing: false,
  importingSuccess: false
};

const defaultState = {
  cases: [],
  importReady: {},
  error: null,
  // GROWTH-6580: why the list request failed ({ status, isNetworkError, requestId }); null while the list is loaded.
  loadError: null,
  // GROWTH-6586: the server's search_error for the last list request ("" when it had none).
  searchError: "",
  preloaders: {
    ...defaultPreloaders
  },
  modals: {
    ...defaultModals
  },
  isShowReferralsTab: true,
  newReferralRequest: {},
  followups: [],
  pagination: {},
  // GROWTH-7293: the matched requests across every page of the tab and filters (cases API `data.matched`); null when
  // the response has no usable number, and then the page counts its loaded cases.
  matched: null,
  updateReferralRequest: false,
  tabsCounter: { purchased: 0, notPurchased: 0 },
  filters: {
    directories: [],
    practices: []
  },
  // GROWTH-8109: My Referrals v2's list. Its own keys, so the previous page's state above is untouched.
  requester: {
    cases: [],
    counts: { all: 0, attention: 0, review: 0, matched: 0, closed: 0 },
    pagination: { currentPage: 1, lastPage: 1, perPage: 10, total: 0 },
    searchError: "",
    loadError: null,
    // list: true until the first answer (or failure) arrives, so the skeleton shows instead of the empty state.
    preloaders: { list: true },
    loaded: false,
    // Requests taken off the list during an Undo window: [{ leadId, index, item }].
    hidden: []
  }
};

// A count the API can be trusted for: a non-negative whole number, as a number or a numeric string. Anything else (an
// older API that sends none, null, "abc", -1, 1.5) is "no total", not 0.
const parseMatched = value => {
  if (typeof value === "string" ? value.trim() === "" : typeof value !== "number") {
    return null;
  }

  const matched = Number(value);

  return Number.isInteger(matched) && matched >= 0 ? matched : null;
};

export default function(state = defaultState, action) {
  const { type, payload } = action;
  switch (type) {
    case referralRequests.GET_CASES_SUCCESS: {
      const parsePage = value => {
        if (value == null || value === "") {
          return 1;
        }
        if (typeof value === "number") {
          return value;
        }
        if (typeof value === "string" && /^\d+$/.test(value)) {
          return parseInt(value, 10);
        }
        return parseInt(queryString.parse(queryString.extract(value)).page, 10) || 1;
      };
      const pagination = payload.pagination || {};
      const perPage = parsePage(pagination.per_page) || 25;
      const total = pagination.total != null && pagination.total !== "" ? Number(pagination.total) : (payload.cases || []).length;
      const apiLastPage = parsePage(pagination.last_page);
      const lastPageNum = Math.max(0, apiLastPage - 1);
      const currentPageNum = Math.max(0, parsePage(pagination.current_page) - 1);

      return {
        ...state,
        preloaders: { ...defaultPreloaders },
        loadError: null,
        searchError: typeof payload.searchError === "string" ? payload.searchError : "",
        cases: payload.cases,
        pagination: { currentPageNum, lastPageNum, perPage, total },
        matched: parseMatched(payload.matched),
        tabsCounter: {
          purchased: payload.tabsCounter.purchased,
          notPurchased: payload.tabsCounter.notpurchased
        },
        isShowReferralsTab: payload.isSelectedFilter || Boolean(payload.tabsCounter.notpurchased),
        filters: payload.filters
      };
    }

    case referralRequests.GET_NEW_REFERRAL_REQUEST:
      return {
        ...state,
        ...payload
      };

    case referralRequests.CLEAR_NEW_REFERRAL_REQUEST:
      return {
        ...state,
        newReferralRequest: {},
        followups: []
      };

    case referralRequests.REQUESTER_CASES_REQUEST:
      return {
        ...state,
        requester: { ...state.requester, preloaders: { list: !payload?.silent || !state.requester.loaded }, loadError: null }
      };

    case referralRequests.REQUESTER_CASES_SUCCESS:
      return {
        ...state,
        requester: {
          ...state.requester,
          // A request waiting out its Undo window stays off the list, whatever an answer in flight says.
          cases: payload.cases.filter(item => !state.requester.hidden.some(entry => entry.leadId === String(item.lead_id))),
          counts: { all: 0, attention: 0, review: 0, matched: 0, closed: 0, ...payload.counts },
          pagination: payload.pagination,
          searchError: payload.searchError,
          loadError: null,
          preloaders: { list: false },
          loaded: true
        }
      };

    case referralRequests.REQUESTER_CASES_FAIL:
      // Kept until the next success, so a retry does not flash the empty state under its skeleton.
      return { ...state, requester: { ...state.requester, preloaders: { list: false }, loaded: true, loadError: payload } };

    case referralRequests.REQUESTER_CASE_PATCH:
      return {
        ...state,
        requester: {
          ...state.requester,
          cases: state.requester.cases.map(item => (String(item.lead_id) === String(payload.leadId) ? { ...item, ...payload.patch } : item))
        }
      };

    case referralRequests.REQUESTER_CASE_HIDE: {
      const index = state.requester.cases.findIndex(item => String(item.lead_id) === String(payload.leadId));
      if (index < 0) {
        return state;
      }
      return {
        ...state,
        requester: {
          ...state.requester,
          cases: state.requester.cases.filter((_, position) => position !== index),
          hidden: [...state.requester.hidden, { leadId: String(payload.leadId), index, item: state.requester.cases[index] }]
        }
      };
    }

    case referralRequests.REQUESTER_CASE_RESTORE: {
      const entry = state.requester.hidden.find(item => item.leadId === String(payload.leadId));
      if (!entry) {
        return state;
      }
      const cases = [...state.requester.cases];
      cases.splice(Math.min(entry.index, cases.length), 0, entry.item);
      return { ...state, requester: { ...state.requester, cases, hidden: state.requester.hidden.filter(item => item !== entry) } };
    }

    case referralRequests.REQUESTER_CASE_FORGET:
      return {
        ...state,
        requester: { ...state.requester, hidden: state.requester.hidden.filter(item => item.leadId !== String(payload.leadId)) }
      };

    case referralRequests.REQUESTER_CLEAR:
      return { ...state, requester: { ...defaultState.requester } };

    case referralRequests.GET_IMPORT_READY:
      return {
        ...state,
        importReady: normalizeImportReady(payload) || { ...EMPTY_IMPORT_READY },
        preloaders: {
          ...state.preloaders,
          importReady: false
        }
      };

    case referralRequests.GET_IMPORT:
      return {
        ...state,
        modals: {
          ...defaultModals,
          importingSuccess: true
        },
        importReady: {},
        preloaders: {
          ...state.preloaders,
          preloaderCaseImport: false
        }
      };

    case referralRequests.GET_CASES_FAIL:
      // GROWTH-6580: kept until the next success, so a retry does not flash the empty state under its spinner.
      return {
        ...state,
        preloaders: { ...defaultPreloaders },
        error: payload,
        loadError: payload,
        searchError: ""
      };

    case referralRequests.GET_IMPORT_FAIL:
      // The import-ready / import calls fail on their own and leave the list as it is.
      return {
        ...state,
        preloaders: { ...defaultPreloaders },
        error: payload
      };

    case referralRequests.SET_CASES_PRELOADER:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          preloaderCase: payload
        }
      };

    case referralRequests.SET_CASES_PRELOADER_IMPORT:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          preloaderCaseImport: payload
        }
      };

    case referralRequests.SET_IMPORT_READY_PRELOADER:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          importReady: payload
        }
      };

    case referralRequests.UPDATE_REFERRAL_REQUEST:
      return {
        ...state,
        updateReferralRequest: payload
      };

    case referralRequests.TOGGLE_ACTIVITY_OF_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: payload.isActive
        }
      };

    case referralRequests.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultModals
        }
      };

    case referralRequests.SET_CLEAR_REFERRALS:
      // GROWTH-6576: the My Request tile reads pagination.total, so clear it with the cases.
      return {
        ...state,
        cases: [],
        pagination: {},
        matched: null,
        loadError: null,
        searchError: "",
        tabsCounter: { purchased: 0, notPurchased: 0, new: 0 }
      };

    default:
      return state;
  }
}
